import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api/client";
import dayjs from "dayjs";

function flattenTops(nodes, tiefe = 0, prefix = "") {
  let out = [];
  (nodes || []).slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .forEach((node, idx) => {
      const nummer = prefix ? prefix + "." + (idx + 1) : String(idx + 1);
      out.push({ top: node, tiefe, nummer });
      if (node.children && node.children.length)
        out = out.concat(flattenTops(node.children, tiefe + 1, nummer));
    });
  return out;
}

const TAB_LABELS = {
  1: "1 · Tagesordnung",
  2: "2 · PDF / Anlage",
  3: "3 · Dokument",
  4: "4 · Info-Screen",
  5: "5 · Reserve",
};

const INFO_SZENEN = [
  { key: "pause", label: "⏸ Pause", beschreibung: "Pausen-Anzeige mit Uhrzeit" },
  { key: "begruessung", label: "👋 Begrüßung", beschreibung: "Willkommens-Screen mit Sitzungstitel" },
  { key: "naechster_top", label: "➡ Nächster TOP", beschreibung: "Zeigt den als Nächstes anstehenden TOP" },
];

function formatGroesse(b) {
  if (!b) return "";
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / 1024 / 1024).toFixed(1) + " MB";
}

export default function ProjektorSteuerung() {
  const { id } = useParams();
  const [tab, setTab] = useState(1);
  const [daten, setDaten] = useState(null);
  const [aktiverTop, setAktiverTop] = useState(null);
  const [dokumente, setDokumente] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [bbEntwurf, setBbEntwurf] = useState("");
  const [begrEntwurf, setBegrEntwurf] = useState("");
  const [beamerToken, setBeamerToken] = useState(null);

  // Beamer-Zugriffs-Token einmal laden (erzeugt beim ersten Mal serverseitig
  // einen neuen) - wird für die eigene Vorschau UND für die generierten
  // Beamer-Links unten gebraucht (siehe Sicherheits-Fix: die Beamer-
  // Endpunkte verlangen jetzt zwingend einen Token statt nur der sitzung_id).
  useEffect(() => {
    api.getBeamerToken(id).then(d => setBeamerToken(d.token)).catch(() => {});
  }, [id]);

  const load = () => api.getProjektorDaten(id, tab, beamerToken).then(d => {
    setDaten(d);
    setAktiverTop(d.aktiver_top?.id || null);
  });

  const ladeDokumente = () => api.getDokumente().then(setDokumente).catch(() => {});

  useEffect(() => {
    if (!beamerToken) return;
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [id, tab, beamerToken]);

  useEffect(() => {
    if (!beamerToken) return;
    api.getProjektorDaten(id, tab, beamerToken).then(d => {
      setBbEntwurf(d.bauchbinde_text || "");
      setBegrEntwurf(d.info_begruessung_text || "");
    });
  }, [id, tab, beamerToken]);

  const setTop = async (topId) => {
    await api.setProjektorStatus(id, { aktiver_top_id: topId, aktive_anlage_id: null, modus: "detail" }, tab);
    setAktiverTop(topId);
    load();
  };

  const setAnlage = async (anlageId) => {
    await api.setProjektorStatus(id, {
      aktiver_top_id: aktiverTop,
      aktive_anlage_id: anlageId,
      modus: anlageId ? "anlage" : "detail",
    }, tab);
    load();
  };

  const pdfSet = (patch) => api.setProjektorPdf(id, patch, tab).then(load);

  const dokumentWaehlen = async (did, projNr) => {
    const url = did ? api.dokumentDateiUrl(did) : null;
    await api.setProjektorStatus(id, { dokument_id: did, dokument_url: url, modus: "dokument" }, projNr);
    load();
  };

  const dokumentUpload = async (file, projNr) => {
    if (!file) return;
    if (file.type !== "application/pdf") { alert("Nur PDF-Dateien"); return; }
    setUploadBusy(true);
    try {
      const neu = await api.uploadDokument(file, file.name);
      await ladeDokumente();
      await dokumentWaehlen(neu.id, projNr);
    } catch (e) {
      alert("Upload fehlgeschlagen: " + (e.detail || e.message || ""));
    } finally {
      setUploadBusy(false);
    }
  };

  const dokumentLoeschen = async (did, titel) => {
    if (!window.confirm(`Dokument "${titel}" aus der Bibliothek loeschen?`)) return;
    await api.deleteDokument(did);
    ladeDokumente();
  };

  const setSzene = async (szene) => {
    await api.setProjektorStatus(id, { info_szene: szene }, 4);
    load();
  };

  const setTyp5 = async (neuerTyp) => {
    await api.setProjektorStatus(id, { typ: neuerTyp }, 5);
    load();
  };

  const bauchbindeSpeichern = async () => {
    await api.setProjektorStatus(id, { bauchbinde_text: bbEntwurf }, tab);
    load();
  };
  
  const begruessungSpeichern = async () => {
    await api.setProjektorStatus(id, { info_begruessung_text: begrEntwurf }, tab);
    load();
  };

  const bauchbindeToggle = async () => {
    await api.setProjektorStatus(id, {
      bauchbinde_text: bbEntwurf,
      bauchbinde_sichtbar: !daten.bauchbinde_sichtbar,
    }, tab);
    load();
  };

  if (!daten) return <div style={{ padding: 40 }}>Lädt…</div>;

  const seite = daten.pdf_seite || 1;
  const zoom = daten.pdf_zoom || 1.0;
  const total = daten.pdf_seiten_gesamt;
  const zeilen = flattenTops(daten.tops);
  const aktiveZeile = zeilen.find(z => z.top.id === aktiverTop);

  const pdfSteuerung = (label) => (
    <div style={{ marginTop: 16, padding: 16, background: "#fff", border: "2px solid #cc0000", borderRadius: 6 }}>
      <h4 style={{ marginTop: 0, marginBottom: 12, color: "#cc0000" }}>📄 PDF-Steuerung — {label}</h4>
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#666", minWidth: 50 }}>Seite:</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_seite: 1 })}>⏮ Anfang</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_seite: Math.max(1, seite - 1) })}>◀ Zurück</button>
        <input type="number" min="1" value={seite}
          onChange={(e) => setDaten({ ...daten, pdf_seite: parseInt(e.target.value) || 1 })}
          onBlur={(e) => pdfSet({ pdf_seite: parseInt(e.target.value) || 1 })}
          onKeyDown={(e) => { if (e.key === "Enter") pdfSet({ pdf_seite: parseInt(e.target.value) || 1 }); }}
          style={{ width: 60, textAlign: "center", padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: 4 }}/>
        <span style={{ color: "#6b7280", fontSize: 13 }}>/ {total || "?"}</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_seite: seite + 1 })}>Vor ▶</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_seite: 9999 })}>Ende ⏭</button>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#666", minWidth: 50 }}>Zoom:</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_zoom: Math.max(0.25, zoom - 0.25) })}>− Kleiner</button>
        <span style={{ minWidth: 50, textAlign: "center", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{Math.round(zoom * 100)}%</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_zoom: Math.min(4, zoom + 0.25) })}>+ Größer</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_zoom: 1.0 })}>100%</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_zoom: 1.5 })}>Breite</button>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
        <span style={{ fontSize: 12, color: "#666", minWidth: 50 }}>Bildlauf:</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_scroll_pct: 0 })}>⏶ Oben</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_scroll_pct: Math.max(0, (daten.pdf_scroll_pct || 0) - 15) })}>▲ Hoch</button>
        <span style={{ minWidth: 50, textAlign: "center", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{Math.round(daten.pdf_scroll_pct || 0)}%</span>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_scroll_pct: Math.min(100, (daten.pdf_scroll_pct || 0) + 15) })}>▼ Runter</button>
        <button className="btn btn-sm btn-secondary" onClick={() => pdfSet({ pdf_scroll_pct: 100 })}>⏷ Unten</button>
      </div>
    </div>
  );

  const topListe = (
    <div className="card">
      <div className="card-header"><h3>Tagesordnung — TOP auswählen</h3></div>
      <div className="card-body" style={{ padding: 0 }}>
        {zeilen.map(({ top, tiefe, nummer }) => (
          <div key={top.id} onClick={() => setTop(top.id)}
            style={{
              padding: "12px 16px", paddingLeft: 16 + tiefe * 28,
              cursor: "pointer", borderBottom: "1px solid #eee",
              background: aktiverTop === top.id ? "#fff3f3" : "white",
              borderLeft: aktiverTop === top.id ? "4px solid var(--red)" : "4px solid transparent",
              display: "flex", alignItems: "center", gap: 12
            }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%",
              background: aktiverTop === top.id ? "var(--red)" : (tiefe > 0 ? "#ddd" : "#eee"),
              color: aktiverTop === top.id ? "white" : "#333",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: tiefe > 0 ? 10 : 12, fontWeight: 700, flexShrink: 0 }}>{nummer}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: aktiverTop === top.id ? 700 : 400 }}>{top.titel}</div>
              <div style={{ fontSize: 11, color: "#888" }}>
                {top.top_typ === "beschluss" ? "🗳 Beschluss" : top.top_typ === "information" ? "ℹ Info" : "• Standard"}
                {!top.oeffentlich && " · 🔒 Nicht öffentlich"}
              </div>
            </div>
            {aktiverTop === top.id && <span style={{ color: "var(--red)", fontWeight: 700 }}>▶ Aktiv</span>}
          </div>
        ))}
      </div>
    </div>
  );

  const dokumentListe = (projNr) => (
    <div className="card">
      <div className="card-header">
        <h3>Dokumenten-Bibliothek</h3>
        <label className="btn btn-primary btn-sm ml-auto" style={{ cursor: "pointer" }}>
          {uploadBusy ? "Lädt hoch…" : "⬆ PDF hochladen"}
          <input type="file" accept="application/pdf" style={{ display: "none" }}
            disabled={uploadBusy}
            onChange={e => { dokumentUpload(e.target.files[0], projNr); e.target.value = ""; }} />
        </label>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        {dokumente.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#999" }}>
            Noch keine Dokumente. Lade ein PDF hoch.
          </div>
        ) : dokumente.map(d => {
          const aktiv = daten.dokument_id === d.id;
          return (
            <div key={d.id}
              style={{
                padding: "12px 16px", borderBottom: "1px solid #eee",
                background: aktiv ? "#fff3f3" : "white",
                borderLeft: aktiv ? "4px solid var(--red)" : "4px solid transparent",
                display: "flex", alignItems: "center", gap: 12
              }}>
              <div style={{ flex: 1, cursor: "pointer" }} onClick={() => dokumentWaehlen(d.id, projNr)}>
                <div style={{ fontWeight: aktiv ? 700 : 400 }}>📄 {d.titel}</div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  {d.dateiname} · {formatGroesse(d.groesse_bytes)}
                  {d.hochgeladen_von && " · " + d.hochgeladen_von}
                </div>
              </div>
              {aktiv && <span style={{ color: "var(--red)", fontWeight: 700 }}>▶ Aktiv</span>}
              <button className="btn btn-ghost btn-sm" onClick={() => dokumentWaehlen(d.id, projNr)}>Anzeigen</button>
              <button className="btn btn-danger btn-sm" onClick={() => dokumentLoeschen(d.id, d.titel)}>✕</button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const bauchbindeBlock = (
    <div style={{ marginTop: 24, padding: 16, background: "#0d1b2a", borderRadius: 6 }}>
      <h4 style={{ marginTop: 0, marginBottom: 12, color: "white" }}>
        📢 Bauchbinde — Projektor {tab}
      </h4>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input type="text" value={bbEntwurf}
          onChange={(e) => setBbEntwurf(e.target.value)}
          placeholder="Text für die Vollbild-Einblendung…"
          style={{ flex: 1, minWidth: 200, padding: "8px 12px", borderRadius: 4, border: "1px solid #444",
                   background: "#1a2a3a", color: "white", fontSize: 14 }} />
        <button className="btn btn-sm btn-secondary" onClick={bauchbindeSpeichern}>Text speichern</button>
        <button className="btn btn-sm"
          onClick={bauchbindeToggle}
          style={{ background: daten.bauchbinde_sichtbar ? "#cc0000" : "#2a8a2a", color: "white" }}>
          {daten.bauchbinde_sichtbar ? "⏹ Ausblenden" : "▶ Einblenden"}
        </button>
      </div>
      <div style={{ fontSize: 12, color: "#8aa", marginTop: 8 }}>
        {daten.bauchbinde_sichtbar
          ? "Einblendung ist AKTIV — wird gerade auf Projektor " + tab + " angezeigt."
          : "Einblendung ist aus. Text eingeben, dann „Einblenden“."}
      </div>
    </div>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>🎬 Projektor-Steuerung</h1>
          <div style={{ fontSize: 14, color: "#666" }}>{daten.sitzung.titel} — {dayjs(daten.sitzung.datum).format("DD.MM.YYYY")}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[1, 2, 3, 4, 5].map(n => (
            <a key={n} href={`/projektor/${id}/ansicht/${n}${beamerToken ? `?token=${encodeURIComponent(beamerToken)}` : ""}`} target="_blank" rel="noreferrer"
               className="btn btn-secondary btn-sm">🖥 P{n}</a>
          ))}
          <button
            className="btn btn-secondary btn-sm"
            title="Macht alle bisher geöffneten Beamer-Links ungültig (z.B. falls versehentlich geteilt) und erzeugt einen neuen Zugriffs-Token"
            onClick={() => {
              if (!window.confirm("Alle bisherigen Beamer-Links werden dadurch ungültig. Fortfahren?")) return;
              api.regenerateBeamerToken(id).then(d => setBeamerToken(d.token));
            }}
          >🔄 Link erneuern</button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #eee", flexWrap: "wrap" }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} onClick={() => setTab(n)}
            style={{
              padding: "10px 16px", border: "none", cursor: "pointer",
              background: tab === n ? "var(--red)" : "transparent",
              color: tab === n ? "white" : "#666",
              fontWeight: tab === n ? 700 : 400, fontSize: 13,
              borderRadius: "6px 6px 0 0",
            }}>
            {TAB_LABELS[n]}
          </button>
        ))}
      </div>

      {(tab === 1 || tab === 2) && (
        <>
          <div className="grid-2">
            {topListe}
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><h3>Projektor {tab} — Status</h3></div>
                <div className="card-body">
                  {daten.aktiver_top ? (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 16, color: "var(--red)" }}>
                        {(aktiveZeile?.nummer || daten.aktiver_top.position)}. {daten.aktiver_top.titel}
                      </div>
                      {daten.aktiver_top.parent_id && (
                        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>↳ Unterpunkt</div>
                      )}
                    </>
                  ) : (
                    <div style={{ color: "#999" }}>Kein TOP aktiv</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {daten.anlagen && daten.anlagen.length > 0 && (
            <div style={{ marginTop: 24, padding: 16, background: "#f5f5f5", borderRadius: 6 }}>
              <h4 style={{ marginTop: 0, marginBottom: 12 }}>Anlagen zum aktiven TOP</h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className={"btn btn-sm " + (!daten.aktive_anlage ? "btn-primary" : "btn-secondary")}
                  onClick={() => setAnlage(null)}>TOP-Ansicht</button>
                {daten.anlagen.map(a => (
                  <button key={a.id}
                    className={"btn btn-sm " + (daten.aktive_anlage && daten.aktive_anlage.id === a.id ? "btn-primary" : "btn-secondary")}
                    onClick={() => setAnlage(a.id)}>📄 {a.dateiname}</button>
                ))}
              </div>
            </div>
          )}

          {daten.aktive_anlage && pdfSteuerung(daten.aktive_anlage.dateiname)}
          {bauchbindeBlock}
        </>
      )}

      {tab === 3 && (
        <>
          {dokumentListe(3)}
          {daten.dokument_id ? (
            pdfSteuerung(dokumente.find(d => d.id === daten.dokument_id)?.titel || "Dokument")
          ) : (
            <div style={{ marginTop: 16, padding: 16, background: "#f5f5f5", borderRadius: 6, color: "#999", textAlign: "center" }}>
              Kein Dokument aktiv — wähle eins aus der Liste oder lade ein neues hoch.
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => dokumentWaehlen(null, 3)}>
              Projektor 3 leeren
            </button>
          </div>
          {bauchbindeBlock}
        </>
      )}

      {tab === 4 && (
        <>
          <div className="card">
            <div className="card-header"><h3>Info-Screen — Szene wählen</h3></div>
            <div className="card-body">
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {INFO_SZENEN.map(sz => {
                  const aktiv = daten.info_szene === sz.key;
                  return (
                    <div key={sz.key} onClick={() => setSzene(sz.key)}
                      style={{
                        padding: "14px 16px", cursor: "pointer", borderRadius: 6,
                        border: aktiv ? "2px solid var(--red)" : "2px solid #eee",
                        background: aktiv ? "#fff3f3" : "white",
                        display: "flex", alignItems: "center", gap: 12
                      }}>
                      <div style={{ fontSize: 20 }}>{sz.label.split(" ")[0]}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: aktiv ? 700 : 600 }}>{sz.label.substring(sz.label.indexOf(" ") + 1)}</div>
                        <div style={{ fontSize: 12, color: "#888" }}>{sz.beschreibung}</div>
                      </div>
                      {aktiv && <span style={{ color: "var(--red)", fontWeight: 700 }}>▶ Aktiv</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header"><h3>Begrüßungstext</h3></div>
            <div className="card-body">
              <input
                type="text"
                value={begrEntwurf}
                onChange={(e) => setBegrEntwurf(e.target.value)}
                placeholder="Herzlich willkommen"
                style={{ width: "100%", padding: "8px 10px", marginBottom: 10 }}
              />
              <button className="btn btn-sm btn-secondary" onClick={begruessungSpeichern}>
                Text speichern
              </button>
            </div>
          </div>
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card-header"><h3>Vorschau-Info</h3></div>
            <div className="card-body" style={{ fontSize: 14, color: "#555" }}>
              <div style={{ marginBottom: 6 }}>
                <strong>Aktuelle Szene:</strong> {INFO_SZENEN.find(s => s.key === daten.info_szene)?.label || daten.info_szene || "—"}
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>Nächster TOP:</strong> {daten.naechster_top
                  ? `${daten.naechster_top.nummer}. ${daten.naechster_top.titel}`
                  : "— (kein nächster TOP)"}
              </div>
              <div style={{ marginBottom: 6 }}>
                <strong>WLAN:</strong> {daten.info_wlan_ssid
                  ? `${daten.info_wlan_ssid} / ${daten.info_wlan_passwort || "(kein Passwort)"}`
                  : "— nicht hinterlegt (in Einstellungen → Info-Screen setzen)"}
              </div>
            </div>
          </div>
          {bauchbindeBlock}
        </>
      )}

      {tab === 5 && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><h3>Reserve-Projektor — Modus</h3></div>
            <div className="card-body">
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  className={"btn btn-sm " + (daten.projektor?.typ !== "dokument" ? "btn-primary" : "btn-secondary")}
                  onClick={() => setTyp5("tagesordnung")}>📋 TOP-Modus</button>
                <button
                  className={"btn btn-sm " + (daten.projektor?.typ === "dokument" ? "btn-primary" : "btn-secondary")}
                  onClick={() => setTyp5("dokument")}>📄 Dokument-Modus</button>
              </div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                {daten.projektor?.typ === "dokument"
                  ? "Projektor 5 zeigt ein freies Dokument."
                  : "Projektor 5 zeigt die Tagesordnung."}
              </div>
            </div>
          </div>

          {daten.projektor?.typ === "dokument" ? (
            <>
              {dokumentListe(5)}
              {daten.dokument_id ? (
                pdfSteuerung(dokumente.find(d => d.id === daten.dokument_id)?.titel || "Dokument")
              ) : (
                <div style={{ marginTop: 16, padding: 16, background: "#f5f5f5", borderRadius: 6, color: "#999", textAlign: "center" }}>
                  Kein Dokument aktiv.
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => dokumentWaehlen(null, 5)}>
                  Projektor 5 leeren
                </button>
              </div>
            </>
          ) : (
            topListe
          )}
          {bauchbindeBlock}
        </>
      )}
    </div>
  );
}
